<template>
  <transition name="cc-banner-slide">
    <div
      v-if="!hasDecided"
      class="cc-banner"
      role="region"
      :aria-label="$t('cookieConsent.bannerAriaLabel')"
    >
      <div class="cc-banner__inner">
        <div class="cc-banner__icon" aria-hidden="true">
          <Cookie :size="28" :stroke-width="1.8" />
        </div>

        <div class="cc-banner__content">
          <p class="cc-banner__title">{{ $t('cookieConsent.bannerTitle') }}</p>
          <p class="cc-banner__text">{{ $t('cookieConsent.bannerText') }}</p>
        </div>

        <div class="cc-banner__actions">
          <button type="button" class="cc-btn cc-btn--outline" @click="openPreferencesModal">
            {{ $t('cookieConsent.managePreferences') }}
          </button>
          <button type="button" class="cc-btn cc-btn--primary" @click="acceptAll">
            {{ $t('cookieConsent.acceptAll') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { defineComponent } from 'vue'
import { useConsent } from '@/composables/useConsent'
import { Cookie } from '@lucide/vue'

export default defineComponent({
  name: 'CookieConsentBanner',
  components: { Cookie },
  setup () {
    const { hasDecided, acceptAll, rejectAll, openPreferencesModal } = useConsent()
    return { hasDecided, acceptAll, rejectAll, openPreferencesModal }
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/brand-tokens';

.cc-banner {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 1080px;
  z-index: 1040;
  padding: 20px 24px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 24px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}

.cc-banner__inner {
  max-width: 1080px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}

.cc-banner__icon {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  color: $brand-color;
  display: flex;
  align-items: center;
  justify-content: center;

}

.cc-banner__content {
  flex: 1 1 180px;
  min-width: 0;
}

.cc-banner__title {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}

.cc-banner__text {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.cc-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: 32px;
}

// ─── Buttons ──────────────────────────────────────────────────────────────────

.cc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &--primary {
    background: $brand-color;
    border: 1.5px solid transparent;
    color: #ffffff;

    &:hover {
      background: color-mix(in srgb, $brand-color 88%, #111827);
    }
  }

  &--outline {
    background: transparent;
    border: 1.5px solid #d1d5db;
    color: #374151;

    &:hover {
      border-color: $brand-color;
      color: $brand-color;
    }
  }

  &--ghost {
    background: transparent;
    border: 1.5px solid transparent;
    color: #6b7280;
    padding: 0 12px;

    &:hover {
      color: #374151;
    }
  }
}

// ─── Animation ────────────────────────────────────────────────────────────────

.cc-banner-slide-enter-active,
.cc-banner-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.cc-banner-slide-enter-from,
.cc-banner-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

// ─── Mobile ───────────────────────────────────────────────────────────────────

@media (max-width: 575px) {
  .cc-banner {
    bottom: calc(85px + env(safe-area-inset-bottom, 0px) + 20px);
  }

  .cc-banner__inner {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .cc-banner__icon {
    display: none;
  }

  .cc-banner__content {
    flex: 0 0 auto;
  }

  .cc-banner__actions {
    flex-direction: column;
    margin-left: 0;

    .cc-btn {
      width: 100%;
      justify-content: center;
    }

    .cc-btn--outline {
      order: 2;
    }

    .cc-btn--primary {
      order: 1;
    }
  }
}
</style>
